import React from "react";
import { Card, CardHeader, Input, Button } from "reactstrap";
import { MEMBER_STATUS_OPTIONS } from "../../../constants/members";
import { GENDER_OPTIONS } from "../../../constants/gender";
import { DURATION_OPTIONS } from "../constants";

/**
 * CrmFilterBar
 * Painel lateral de filtros no mesmo estilo do MasterSidebar (Card sticky).
 * Todos os filtros ficam visíveis, botão "Buscar" no final.
 */
const CrmFilterBar = ({
    filters,
    updateFilter,
    resetFilters,
    onSearch,
    memberships,
    activities,
    employees,
    loading,
    loadingRefs,
}) => {
    const allEmployees = employees || [];

    const handleKeyDown = (e) => {
        if (e.key === "Enter") onSearch();
    };

    return (
        <Card
            className="border-0 shadow-sm overflow-hidden d-flex flex-column rounded-0"
            style={{ position: "sticky", top: "85px", maxHeight: "calc(100vh - 100px)" }}
        >
            {/* HEADER */}
            <CardHeader className="bg-white border-bottom py-3 px-4 flex-shrink-0">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center overflow-hidden">
                        <div className="avatar-xs me-2 flex-shrink-0">
                            <span className="avatar-title bg-soft-primary text-primary rounded">
                                <i className="mdi mdi-filter-variant font-size-16"></i>
                            </span>
                        </div>
                        <div className="overflow-hidden">
                            <h6 className="mb-0 fw-bold text-dark text-truncate">CRM - Filtros</h6>
                            <p className="text-muted small mb-0 text-truncate">Segmentação de clientes</p>
                        </div>
                    </div>
                </div>
            </CardHeader>

            {/* FILTROS SCROLLÁVEIS */}
            <div className="flex-grow-1 overflow-auto custom-scrollbar p-4 d-flex flex-column gap-3">

                {/* Busca Rápida */}
                <FilterField label="Busca Rápida" icon="mdi-magnify">
                    <div className="search-box position-relative">
                        <Input
                            type="text"
                            className="form-control bg-light border-light rounded-0 font-size-13 py-2 ps-5"
                            placeholder="Nome, Email, CPF ou Telefone..."
                            value={filters.search}
                            onChange={e => updateFilter("search", e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <i className="mdi mdi-magnify search-icon position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                    </div>
                </FilterField>

                {/* Situação */}
                <FilterField label="Status" icon="mdi-account-check-outline">
                    <Input
                        type="select"
                        bsSize="sm"
                        value={filters.status}
                        onChange={e => updateFilter("status", e.target.value)}
                        className="form-select bg-light border-light font-size-13"
                    >
                        <option value="">Todos</option>
                        {MEMBER_STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Input>
                </FilterField>

                {/* Sexo */}
                <FilterField label="Sexo" icon="mdi-gender-male-female">
                    <Input
                        type="select"
                        bsSize="sm"
                        value={filters.gender}
                        onChange={e => updateFilter("gender", e.target.value)}
                        className="form-select bg-light border-light font-size-13"
                    >
                        <option value="">Todos</option>
                        {GENDER_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Input>
                </FilterField>

                {/* Contrato / Plano */}
                <FilterField label="Contrato / Plano" icon="mdi-file-document-outline">
                    <Input
                        type="select"
                        bsSize="sm"
                        value={filters.membershipId}
                        onChange={e => updateFilter("membershipId", e.target.value)}
                        className="form-select bg-light border-light font-size-13"
                        disabled={loadingRefs}
                    >
                        <option value="">Qualquer Contrato</option>
                        {(memberships || []).map(m => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </Input>
                </FilterField>

                {/* Duração do Plano */}
                <FilterField label="Duração do Plano" icon="mdi-clock-outline">
                    <Input
                        type="select"
                        bsSize="sm"
                        value={filters.durationType}
                        onChange={e => updateFilter("durationType", e.target.value)}
                        className="form-select bg-light border-light font-size-13"
                    >
                        {DURATION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Input>
                </FilterField>

                {/* Vencimento do Contrato */}
                <FilterField label="Vencimento do Contrato" icon="mdi-calendar-range">
                    <div className="d-flex gap-2">
                        <Input
                            type="date"
                            bsSize="sm"
                            value={filters.contractStartDate}
                            onChange={e => updateFilter("contractStartDate", e.target.value)}
                            className="bg-light border-light font-size-12"
                        />
                        <Input
                            type="date"
                            bsSize="sm"
                            value={filters.contractEndDate}
                            onChange={e => updateFilter("contractEndDate", e.target.value)}
                            className="bg-light border-light font-size-12"
                        />
                    </div>
                </FilterField>

                {/* Atividade / Modalidade */}
                <FilterField label="Atividade / Modalidade" icon="mdi-dumbbell">
                    <Input
                        type="select"
                        bsSize="sm"
                        value={filters.activityId}
                        onChange={e => updateFilter("activityId", e.target.value)}
                        className="form-select bg-light border-light font-size-13"
                        disabled={loadingRefs}
                    >
                        <option value="">Todas</option>
                        {(activities || []).map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                    </Input>
                </FilterField>

                {/* Faixa Etária */}
                <FilterField label="Faixa Etária" icon="mdi-cake-variant-outline">
                    <div className="d-flex gap-2">
                        <Input
                            type="number"
                            bsSize="sm"
                            placeholder="Min"
                            value={filters.ageMin}
                            onChange={e => updateFilter("ageMin", e.target.value)}
                            className="bg-light border-light font-size-13"
                            min={0}
                            max={120}
                        />
                        <Input
                            type="number"
                            bsSize="sm"
                            placeholder="Max"
                            value={filters.ageMax}
                            onChange={e => updateFilter("ageMax", e.target.value)}
                            className="bg-light border-light font-size-13"
                            min={0}
                            max={120}
                        />
                    </div>
                </FilterField>

                {/* Professor */}
                <FilterField label="Professor" icon="mdi-human-male-board">
                    <Input
                        type="select"
                        bsSize="sm"
                        value={filters.employeeId}
                        onChange={e => updateFilter("employeeId", e.target.value)}
                        className="form-select bg-light border-light font-size-13"
                        disabled={loadingRefs}
                    >
                        <option value="">Todos</option>
                        {allEmployees.map(e => (
                            <option key={e.id} value={e.id}>
                                {e.fullName || e.name || `${e.firstName || ""} ${e.lastName || ""}`.trim()}
                            </option>
                        ))}
                    </Input>
                </FilterField>

                {/* Consultor (Venda) */}
                <FilterField label="Consultor (Venda)" icon="mdi-account-tie-outline">
                    <Input
                        type="select"
                        bsSize="sm"
                        value={filters.consultantId}
                        onChange={e => updateFilter("consultantId", e.target.value)}
                        className="form-select bg-light border-light font-size-13"
                        disabled={loadingRefs}
                    >
                        <option value="">Todos</option>
                        {allEmployees.map(e => (
                            <option key={e.id} value={e.id}>
                                {e.fullName || e.name || `${e.firstName || ""} ${e.lastName || ""}`.trim()}
                            </option>
                        ))}
                    </Input>
                </FilterField>

                {/* Data da Venda (Conversão) */}
                <FilterField label="Data da Venda (Conversão)" icon="mdi-cart-check">
                    <div className="d-flex gap-2">
                        <Input
                            type="date"
                            bsSize="sm"
                            value={filters.saleStartDate}
                            onChange={e => updateFilter("saleStartDate", e.target.value)}
                            className="bg-light border-light font-size-12"
                        />
                        <Input
                            type="date"
                            bsSize="sm"
                            value={filters.saleEndDate}
                            onChange={e => updateFilter("saleEndDate", e.target.value)}
                            className="bg-light border-light font-size-12"
                        />
                    </div>
                </FilterField>
            </div>

            {/* FOOTER: BOTÕES DE AÇÃO */}
            <div className="border-top px-4 py-3 bg-white flex-shrink-0 d-flex gap-2">
                <Button
                    color="primary"
                    className="flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                    onClick={onSearch}
                    disabled={loading}
                >
                    {loading ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                    ) : (
                        <i className="mdi mdi-magnify font-size-16"></i>
                    )}
                    Buscar
                </Button>
                <Button
                    color="light"
                    className="px-3"
                    onClick={resetFilters}
                    title="Limpar filtros"
                >
                    <i className="mdi mdi-filter-remove-outline font-size-16 text-muted"></i>
                </Button>
            </div>
        </Card>
    );
};

/**
 * FilterField — Wrapper padronizado para cada campo de filtro.
 */
const FilterField = ({ label, icon, children }) => (
    <div>
        <label className="form-label text-muted fw-medium mb-1" style={{ fontSize: "11.5px" }}>
            <i className={`mdi ${icon} me-1`}></i>
            {label}
        </label>
        {children}
    </div>
);

export default CrmFilterBar;
